<!-- RichTextFormatMenu.vue -->
<!--
  Um dropdown só, com subníveis, no lugar dos três seletores da barra (bloco,
  família e corpo da fonte).

  Por que juntar: os três ocupavam metade da barra e eram a única coisa nela com
  rótulo em texto. Reunidos, sobra espaço para os botões de ícone e a barra deixa
  de quebrar em duas linhas tão cedo.

  O que se perde ao juntar — ver os três valores correntes de relance — é
  devolvido no próprio menu: cada linha mostra o valor atual ao lado da seta.
  Assim a informação continua a um clique, e não a três.

  Props:
    groups   — [{ key, label, modelValue, options: [{ value, label }] }]
    disabled

  Emits:
    select — { key, value }
-->
<script setup>
import { computed, onBeforeUnmount, onMounted, nextTick, ref } from "vue";

const props = defineProps({
  groups: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
  label: { type: String, default: "Formato" },
});

const emit = defineEmits(["select"]);

const isOpen = ref(false);
const openGroupKey = ref(null);
const flipLeft = ref(false);
const rootEl = ref(null);
const menuEl = ref(null);

// Rótulo do valor corrente de cada grupo. Valor sem correspondência cai na
// primeira opção, que é sempre o padrão ("Parágrafo", "Fonte padrão"…).
const currentLabels = computed(() => {
  const map = {};
  for (const group of props.groups) {
    const found = group.options.find((o) => o.value === group.modelValue);
    map[group.key] = (found ?? group.options[0])?.label ?? "";
  }
  return map;
});

function closeAll() {
  isOpen.value = false;
  openGroupKey.value = null;
}

function toggle() {
  if (props.disabled) return;
  if (isOpen.value) return closeAll();
  isOpen.value = true;
  openGroupKey.value = null;
}

/**
 * Abre o subnível e decide o lado.
 *
 * O submenu sai à direita por padrão. Perto da borda da janela ele passaria do
 * limite e o fim da lista ficaria inalcançável — o mesmo defeito que o menu de
 * ações do TextForm tinha. A medida só existe depois de o `v-if` renderizar,
 * daí o `nextTick`.
 */
async function openGroup(key) {
  openGroupKey.value = openGroupKey.value === key ? null : key;
  if (!openGroupKey.value) return;

  await nextTick();
  const menu = menuEl.value?.getBoundingClientRect();
  const sub = menuEl.value
    ?.querySelector(".rte-fmt-sub")
    ?.getBoundingClientRect();
  if (!menu || !sub) return;

  flipLeft.value = menu.right + sub.width + 8 > window.innerWidth;
}

function choose(key, value) {
  closeAll();
  emit("select", { key, value });
}

function onClickOutside(event) {
  if (isOpen.value && !rootEl.value?.contains(event.target)) closeAll();
}

function onKeydown(event) {
  if (event.key !== "Escape" || !isOpen.value) return;
  // Esc fecha um nível de cada vez: primeiro o subnível, depois o menu.
  if (openGroupKey.value) openGroupKey.value = null;
  else closeAll();
  event.stopPropagation();
}

onMounted(() => document.addEventListener("click", onClickOutside));
onBeforeUnmount(() => document.removeEventListener("click", onClickOutside));
</script>

<template>
  <!--
    `mousedown.prevent` em toda a raiz: sem ele o clique tira o foco do editor
    ANTES do comando rodar, e a seleção do usuário se perde. O `click` continua
    disparando — só a mudança de foco é impedida.
  -->
  <div ref="rootEl" class="rte-fmt" @mousedown.prevent @keydown="onKeydown">
    <button
      type="button"
      class="rte-fmt-btn"
      :class="{ 'rte-fmt-btn--open': isOpen }"
      :disabled="disabled"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      title="Formato do texto"
      @click="toggle"
    >
      <i class="fas fa-font rte-fmt-icon"></i>
      <span class="rte-fmt-label">{{ label }}</span>
      <i class="fas fa-chevron-down rte-fmt-chevron" :class="{ open: isOpen }"></i>
    </button>

    <div v-if="isOpen" ref="menuEl" class="rte-fmt-menu" role="menu">
      <ul>
        <li
          v-for="group in groups"
          :key="group.key"
          class="rte-fmt-row"
          :class="{ 'rte-fmt-row--open': openGroupKey === group.key }"
          role="menuitem"
          :aria-haspopup="true"
          :aria-expanded="openGroupKey === group.key"
          @click.stop="openGroup(group.key)"
        >
          <span class="rte-fmt-row-label">{{ group.label }}</span>
          <span class="rte-fmt-row-value">{{ currentLabels[group.key] }}</span>
          <i class="fas fa-chevron-right rte-fmt-arrow"></i>

          <div
            v-if="openGroupKey === group.key"
            class="rte-fmt-sub"
            :class="{ 'rte-fmt-sub--left': flipLeft }"
            role="menu"
          >
            <ul>
              <li
                v-for="option in group.options"
                :key="option.value"
                class="rte-fmt-option"
                :class="{
 'rte-fmt-option--current': option.value === group.modelValue,
                }"
                role="menuitemradio"
                :aria-checked="option.value === group.modelValue"
                @click.stop="choose(group.key, option.value)"
              >
                <span>{{ option.label }}</span>
                <i
                  v-if="option.value === group.modelValue"
                  class="fas fa-check rte-fmt-check"
                ></i>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.rte-fmt {
  position: relative;
  display: inline-flex;
}

/* Mesma altura e mesmo raio dos botões da barra: é um controle entre controles. */
.rte-fmt-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 2rem;
  padding: 0 0.45rem;
  border-radius: 0.35rem;
  border: 1px solid transparent;
  background: transparent;
  font-family: inherit;
  font-size: 0.85rem;
  line-height: 1;
  color: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.rte-fmt-btn:hover:not(:disabled),
.rte-fmt-btn--open {
  background: rgba(128, 128, 128, 0.16);
}

.rte-fmt-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rte-fmt-icon {
  font-size: 0.8rem;
  opacity: 0.8;
}

.rte-fmt-chevron {
  font-size: 0.6rem;
  opacity: 0.6;
  transition: transform 0.2s ease-in-out;
}

.rte-fmt-chevron.open {
  transform: rotate(180deg);
}

/* Cantos e sombra do padrao do projeto — os mesmos do dropdown "total de notas". */
.rte-fmt-menu,
.rte-fmt-sub {
  position: absolute;
  z-index: 100;
  background: var(--rte-bg, var(--surface));
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
}

.rte-fmt-menu {
  left: 0;
  top: calc(100% + 4px);
  min-width: 12rem;
}

.rte-fmt-menu ul,
.rte-fmt-sub ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}

.rte-fmt-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-radius: 7px;
  font-size: 0.82rem;
  white-space: nowrap;
  cursor: pointer;
}

.rte-fmt-row:hover,
.rte-fmt-row--open {
  background: rgba(128, 128, 128, 0.14);
}

.rte-fmt-row-label {
  flex: 1 1 auto;
}

/* O valor corrente é informação secundária: apagado, para o rótulo do grupo
   continuar sendo o que se lê primeiro. */
.rte-fmt-row-value {
  font-size: 0.75rem;
  opacity: 0.6;
}

.rte-fmt-arrow {
  font-size: 0.6rem;
  opacity: 0.5;
}

/* Subnível ancorado no TOPO da linha que o abriu, para a relação entre os dois
   ficar visível. O deslocamento de 4px repete o vão do menu principal. */
.rte-fmt-sub {
  left: calc(100% + 4px);
  top: -0.3rem;
  min-width: 10rem;
}

.rte-fmt-sub--left {
  left: auto;
  right: calc(100% + 4px);
}

.rte-fmt-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.4rem 0.6rem;
  border-radius: 7px;
  font-size: 0.82rem;
  white-space: nowrap;
  cursor: pointer;
}

.rte-fmt-option:hover {
  background: rgba(128, 128, 128, 0.14);
}

.rte-fmt-option--current {
  font-weight: 600;
}

.rte-fmt-check {
  font-size: 0.7rem;
  opacity: 0.7;
}

.dark .rte-fmt-menu,
.dark .rte-fmt-sub {
  border-color: rgba(255, 255, 255, 0.14);
}
</style>
